<template>
  <v-data-table
    :headers="headers"
    :items="invoices"
    :loading="loading"
    :items-per-page="25"
    sort-by="invoiceDate"
    sort-desc
    :hide-default-header="$vuetify.breakpoint.xsOnly"
    :no-data-text="noDataText"
  >
    <template v-slot:body="{ items }">
      <tbody v-if="!items.length && !loading">
        <tr>
          <td :colspan="headers.length" class="text-center text--secondary py-4">
            {{ noDataText }}
          </td>
        </tr>
      </tbody>
      <!-- Mobiili: lasku riveineen yhtenä korttimaisena soluna, koska sarakkeet eivät mahdu puhelimen leveyteen -->
      <template v-if="$vuetify.breakpoint.xsOnly">
        <tbody v-for="inv in items" :key="inv.netvisorKey" class="invoice-group">
          <tr>
            <td class="mobile-invoice">
              <div class="d-flex align-start">
                <div class="flex-grow-1">
                  <div class="font-weight-medium">{{ inv.vendorName }}</div>
                  <div class="caption text--secondary">
                    {{ inv.invoiceNumber }} · {{ formatDate(inv.invoiceDate) }} · {{ inv.invoiceStatus }}
                  </div>
                </div>
                <v-btn
                  v-if="inv.netvisorUrl"
                  icon
                  :href="inv.netvisorUrl"
                  target="_blank"
                  :aria-label="$t('sites.purchase_invoices.open_in_netvisor')"
                  :title="$t('sites.purchase_invoices.open_in_netvisor')"
                >
                  <v-icon>mdi-open-in-new</v-icon>
                </v-btn>
              </div>
              <div class="d-flex justify-space-between mt-1">
                <span class="caption text--secondary">{{ allocatedLabel }}</span>
                <span>{{ formatCurrency(inv.siteAllocatedAmount) }}</span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="caption text--secondary">{{ $t('sites.purchase_invoices.total') }}</span>
                <span>{{ formatCurrency(inv.totalAmount) }}</span>
              </div>
              <div v-for="(line, li) in inv.lines" :key="`${inv.netvisorKey}-${line.id || li}`" class="mt-2 caption">
                <div class="d-flex justify-space-between">
                  <span>
                    {{ lineText(line) }}
                    <span v-if="lineProductInfo(line)" class="text--secondary">({{ lineProductInfo(line) }})</span>
                  </span>
                  <span class="ml-2 text-no-wrap">{{ formatCurrency(line.lineSum) }}</span>
                </div>
                <div v-if="bookable" class="d-flex align-center mt-1">
                  <v-chip small :color="statusColor(line)" dark>{{ statusLabel(line) }}</v-chip>
                  <span class="ml-2">{{ bookedText(line) }}</span>
                  <v-spacer />
                  <v-btn
                    v-if="canBook(line) && line.bookingStatus === 'NOT_BOOKED'"
                    small
                    outlined
                    color="primary"
                    @click="$emit('book', inv, line)"
                  >
                    {{ $t('inventory.purchase_invoices.book') }}
                  </v-btn>
                  <purchase-line-menu
                    v-if="$isTenantAdmin && line.netvisorLineKey"
                    :line="line"
                    @book="$emit('book', inv, line)"
                    @set-status="status => $emit('set-status', inv, line, status)"
                    @clear-status="$emit('clear-status', inv, line)"
                  />
                </div>
                <div v-if="bookable && line.bookingNote" class="font-italic text--secondary">{{ line.bookingNote }}</div>
                <template v-if="bookable">
                  <div v-for="booking in line.bookings" :key="`b-${booking.transactionId}`" class="text--secondary">
                    <v-icon x-small class="no-gradient">mdi-subdirectory-arrow-right</v-icon>
                    {{ bookingText(booking) }}
                  </div>
                </template>
              </div>
              <template v-if="bookable">
                <div v-for="booking in inv.orphanBookings || []" :key="`o-${booking.transactionId}`" class="mt-2 caption orange--text text--darken-3">
                  <v-icon x-small color="orange darken-3" class="no-gradient">mdi-alert</v-icon>
                  {{ $t('inventory.purchase_invoices.orphan_booking') }}: {{ bookingText(booking) }}
                </div>
              </template>
            </td>
          </tr>
        </tbody>
        <tbody v-if="items.length">
          <tr class="total-row">
            <td class="d-flex justify-space-between font-weight-bold">
              <span>{{ $t('sites.purchase_invoices.total_allocated') }}</span>
              <span>{{ formatCurrency(totalAllocated) }}</span>
            </td>
          </tr>
        </tbody>
      </template>
      <template v-else>
        <!-- Yksi <tbody> per lasku: perustiedot + riviselite ovat sama hoverattava ryhmä -->
        <tbody v-for="inv in items" :key="inv.netvisorKey" class="invoice-group">
          <tr>
            <td>{{ inv.vendorName }}</td>
            <td>{{ inv.invoiceNumber }}</td>
            <td>{{ formatDate(inv.invoiceDate) }}</td>
            <td class="text-end">{{ formatCurrency(inv.siteAllocatedAmount) }}</td>
            <td class="text-end">{{ formatCurrency(inv.totalAmount) }}</td>
            <td>{{ inv.invoiceStatus }}</td>
            <td class="text-end">
              <v-btn
                v-if="inv.netvisorUrl"
                icon
                :href="inv.netvisorUrl"
                target="_blank"
                :aria-label="$t('sites.purchase_invoices.open_in_netvisor')"
                :title="$t('sites.purchase_invoices.open_in_netvisor')"
              >
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </td>
          </tr>
          <!-- Kohteelle kohdistetut laskurivit: selite + rivin summa kohdistettu-sarakkeessa -->
          <template v-for="(line, li) in inv.lines">
            <tr :key="`${inv.netvisorKey}-${line.id || li}`" class="line-row text--secondary caption">
              <td :colspan="3" class="line-title-cell">
                {{ lineText(line) }}
                <span v-if="lineProductInfo(line)">({{ lineProductInfo(line) }})</span>
              </td>
              <td class="text-end">{{ formatCurrency(line.lineSum) }}</td>
              <td v-if="!bookable" :colspan="3"></td>
              <td v-else :colspan="3" class="text-end">
                <v-chip small :color="statusColor(line)" dark class="mr-2">{{ statusLabel(line) }}</v-chip>
                <span>{{ bookedText(line) }}</span>
                <span v-if="line.bookingNote" class="ml-2 font-italic">{{ line.bookingNote }}</span>
                <v-btn
                  v-if="canBook(line) && line.bookingStatus === 'NOT_BOOKED'"
                  small
                  outlined
                  color="primary"
                  class="ml-2"
                  @click="$emit('book', inv, line)"
                >
                  {{ $t('inventory.purchase_invoices.book') }}
                </v-btn>
                <purchase-line-menu
                  v-if="$isTenantAdmin && line.netvisorLineKey"
                  :line="line"
                  @book="$emit('book', inv, line)"
                  @set-status="status => $emit('set-status', inv, line, status)"
                  @clear-status="$emit('clear-status', inv, line)"
                />
              </td>
            </tr>
            <template v-if="bookable">
              <tr v-for="booking in line.bookings" :key="`b-${booking.transactionId}`" class="line-row caption">
                <td :colspan="7" class="booking-cell text--secondary">
                  <v-icon x-small class="no-gradient">mdi-subdirectory-arrow-right</v-icon>
                  {{ bookingText(booking) }}
                </td>
              </tr>
            </template>
          </template>
          <template v-if="bookable">
            <tr v-for="booking in inv.orphanBookings || []" :key="`o-${booking.transactionId}`" class="line-row caption">
              <td :colspan="7" class="booking-cell orange--text text--darken-3">
                <v-icon x-small color="orange darken-3" class="no-gradient">mdi-alert</v-icon>
                {{ $t('inventory.purchase_invoices.orphan_booking') }}: {{ bookingText(booking) }}
              </td>
            </tr>
          </template>
        </tbody>
        <tbody v-if="items.length">
          <tr class="total-row">
            <td :colspan="3" class="text-right font-weight-bold">
              {{ $t('sites.purchase_invoices.total_allocated') }}
            </td>
            <td class="text-end font-weight-bold">{{ formatCurrency(totalAllocated) }}</td>
            <td colspan="3"></td>
          </tr>
        </tbody>
      </template>
    </template>
  </v-data-table>
</template>

<script>
import PurchaseLineMenu from '@/components/Netvisor/PurchaseLineMenu';
import { PIECES_UNIT } from '@/helpers/inventoryFormat';

/**
 * Netvisorin ostolaskutaulukko: yksi lasku = perustiedot + kohteelle (työmaa tai varasto)
 * kohdistetut rivit. Käytetään työmaan ostolaskuissa ja varastonhallinnassa.
 */
export default {
  name: 'NetvisorPurchaseInvoiceTable',
  components: { PurchaseLineMenu },
  props: {
    invoices: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Kohdistettu-sarakkeen otsikko, esim. "Työmaalle kohdistettu" / "Varastolle kohdistettu"
    allocatedLabel: {
      type: String,
      required: true,
    },
    noDataText: {
      type: String,
      required: true,
    },
    // Varaston ostolaskut: rivin kirjaustila, kirjaukset ja kirjaustoiminnot
    bookable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headers() {
      return [
        { text: this.$t('sites.purchase_invoices.vendor'), value: 'vendorName' },
        { text: this.$t('sites.purchase_invoices.invoice_number'), value: 'invoiceNumber' },
        { text: this.$t('sites.purchase_invoices.date'), value: 'invoiceDate' },
        { text: this.allocatedLabel, value: 'siteAllocatedAmount', align: 'end' },
        { text: this.$t('sites.purchase_invoices.total'), value: 'totalAmount', align: 'end' },
        { text: this.$t('sites.purchase_invoices.status'), value: 'invoiceStatus' },
        { text: '', value: 'netvisorUrl', sortable: false, align: 'end' },
      ];
    },
    totalAllocated() {
      return this.invoices.reduce((sum, inv) => sum + (Number(inv.siteAllocatedAmount) || 0), 0);
    },
  },
  methods: {
    canBook(line) {
      return this.bookable && this.$isTenantAdmin && !!line.netvisorLineKey;
    },
    statusLabel(line) {
      if (line.bookingStatus === 'BOOKED') return this.$t('inventory.purchase_invoices.status_booked');
      if (line.bookingStatus === 'HANDLED') return this.$t('inventory.purchase_invoices.status_handled');
      return Number(line.bookedValue) > 0
        ? this.$t('inventory.purchase_invoices.status_partial')
        : this.$t('inventory.purchase_invoices.status_not_booked');
    },
    statusColor(line) {
      if (line.bookingStatus === 'BOOKED') return 'success';
      if (line.bookingStatus === 'HANDLED') return 'grey';
      return 'warning';
    },
    // "kirjattu / veroton summa"
    bookedText(line) {
      const net = line.lineNetSum !== null && line.lineNetSum !== undefined ? this.formatCurrency(line.lineNetSum) : '–';
      return `${this.formatCurrency(line.bookedValue || 0)} / ${net}`;
    },
    bookingText(booking) {
      const quantity = Number(booking.quantity).toFixed(3).replace(/\.?0+$/, '');
      return [booking.itemName, booking.warehouseName, `${quantity} ${booking.unit || PIECES_UNIT}`,
        this.formatCurrency(booking.value), this.formatDate(booking.date)].join(' · ');
    },
    lineText(line) {
      return line.description || line.productName || line.productCode || '';
    },
    // Tuotetieto sulkuihin selitteen perään; jos selitettä ei ole, tuotenimi on jo rivin tekstinä.
    lineProductInfo(line) {
      if (!line.description) return '';
      return [line.productCode, line.productName].filter(Boolean).join(' ');
    },

    formatDate(d) {
      if (!d) return '';
      if (Array.isArray(d)) {
        const [y, m, day] = d;
        return `${String(day).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
      }
      const s = String(d).substr(0, 10);
      const [y, m, day] = s.split('-');
      return day && m && y ? `${day}.${m}.${y}` : s;
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '';
      return Number(value).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
    },
  },
};
</script>

<style scoped>
/* Yksi lasku = yksi <tbody>. Sisäiset reunaviivat pois, erotin vain ryhmän
   viimeiselle riville, ja koko ryhmä korostuu yhtenä hoverissa. */
.invoice-group td {
  border-bottom: none !important;
}
.invoice-group tr:last-child td {
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}
/* Estetään Vuetijn rivikohtainen hover, jotta perustietorivi ei tummene
   selite-riviä enempää - sama sävy molemmille. */
.invoice-group tr:hover {
  background: transparent !important;
}
.invoice-group:hover td {
  background-color: rgba(0, 0, 0, 0.04);
}
.line-row td {
  height: auto !important;
  padding-top: 0 !important;
  padding-bottom: 8px !important;
}
/* Sisennys kertoo, että rivi kuuluu yllä olevaan laskuun. */
.line-title-cell {
  padding-left: 32px !important;
}
.booking-cell {
  padding-left: 48px !important;
}
/* Mobiilissa lasku on yksi solu, jonka sisältö rivittyy vapaasti. */
.mobile-invoice {
  height: auto !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  white-space: normal;
}
</style>
